'use client';

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { SetupStoreLogoMark } from '@/components/setup/experience/SetupStoreLogoMark';
import { SetupStepSection } from '@/components/setup/experience/SetupStepSection';
import { cn } from '@/lib/utils';

interface SetupConnectPlatformMark {
  logoSrc: string;
  logoDarkSrc?: string;
  name: string;
  variant?: 'integration' | 'brand';
}

interface SetupConnectStoreMark {
  name: string;
  primaryColor: string;
  logoUrl?: string;
}

interface SetupConnectStepSectionProps {
  description?: string;
  statusBadge?: string;
  statusMeta?: string;
  title?: string;
  platform?: SetupConnectPlatformMark;
  platformInTitle?: boolean;
  storeMark?: SetupConnectStoreMark;
  storeMarkInTitle?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}

function ConnectPlatformLogo({
  platform,
  className,
}: {
  platform: SetupConnectPlatformMark;
  className?: string;
}) {
  const isBrandLogo = platform.variant === 'brand';

  return (
    <span
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-[0.625rem] border border-[color-mix(in_srgb,var(--border)_80%,transparent)] bg-[color-mix(in_srgb,var(--muted)_70%,var(--card))]',
        className,
      )}
      aria-hidden
    >
      {platform.logoDarkSrc ? (
        <>
          <img
            src={platform.logoSrc}
            alt=""
            className={cn('h-6 w-6 object-contain', isBrandLogo && 'dark:hidden')}
            draggable={false}
          />
          <img
            src={platform.logoDarkSrc}
            alt=""
            className={cn('hidden h-6 w-6 object-contain dark:block')}
            draggable={false}
          />
        </>
      ) : (
        <img src={platform.logoSrc} alt="" className="h-6 w-6 object-contain" draggable={false} />
      )}
    </span>
  );
}

function ConnectStoreTitleMark({ storeMark }: { storeMark: SetupConnectStoreMark }) {
  return (
    <SetupStoreLogoMark
      logoUrl={storeMark.logoUrl}
      primaryColor={storeMark.primaryColor}
      size="md"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[0.625rem] border-0 bg-[color-mix(in_srgb,var(--muted)_70%,var(--card))]"
    />
  );
}

export function SetupConnectStepSection({
  description,
  statusBadge,
  statusMeta,
  title,
  platform,
  platformInTitle = false,
  storeMark,
  storeMarkInTitle = false,
  children,
  footer,
}: SetupConnectStepSectionProps) {
  const { t } = useTranslation('setup');
  const isBrandLogo = platform?.variant === 'brand';

  const titleAdornment =
    platform && platformInTitle ? (
      <ConnectPlatformLogo platform={platform} />
    ) : storeMark && storeMarkInTitle ? (
      <ConnectStoreTitleMark storeMark={storeMark} />
    ) : undefined;

  return (
    <SetupStepSection
      title={title ?? t('experience.shell.stepTitles.store')}
      description={description}
      statusBadge={statusBadge}
      statusMeta={statusMeta}
      titleAdornment={titleAdornment}
      footer={footer}
    >
      {platform && !platformInTitle ? (
        <div
          className="border-border mb-5 inline-flex items-center gap-2 rounded-full border bg-[color-mix(in_srgb,var(--muted)_25%,var(--card))] py-1.5 pl-1.5 pr-2.5"
          aria-hidden
        >
          <span className="bg-muted flex h-7 w-7 items-center justify-center overflow-hidden rounded-full">
            {platform.logoDarkSrc ? (
              <>
                <img
                  src={platform.logoSrc}
                  alt=""
                  className={cn('h-[1.125rem] w-[1.125rem] object-contain', isBrandLogo && 'dark:hidden')}
                  draggable={false}
                />
                <img
                  src={platform.logoDarkSrc}
                  alt=""
                  className={cn('hidden h-[1.125rem] w-[1.125rem] object-contain dark:block')}
                  draggable={false}
                />
              </>
            ) : (
              <img
                src={platform.logoSrc}
                alt=""
                className="h-[1.125rem] w-[1.125rem] object-contain"
                draggable={false}
              />
            )}
          </span>
          <span className="text-foreground pr-1 text-xs font-semibold">{platform.name}</span>
        </div>
      ) : null}

      {storeMark && !storeMarkInTitle ? (
        <div
          className="border-border mb-5 inline-flex items-center gap-2 rounded-full border bg-[color-mix(in_srgb,var(--muted)_25%,var(--card))] py-1.5 pl-1.5 pr-2.5"
          aria-hidden
        >
          <SetupStoreLogoMark
            logoUrl={storeMark.logoUrl}
            primaryColor={storeMark.primaryColor}
            size="md"
            className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-transparent"
          />
          <span className="text-foreground pr-1 text-xs font-semibold">{storeMark.name}</span>
        </div>
      ) : null}

      {children}
    </SetupStepSection>
  );
}
